import { Fragment, useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { ApiError, apiFetch } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import {
  FETCH_TYPES,
  FETCH_TYPE_LABEL,
  fmtFecha,
  inputClass,
  labelClass,
  traducirError,
  type FetchType,
  type Source,
} from './tipos';

const MENSAJES_ERROR: Record<string, string> = {
  code_duplicado: 'Ya existe una fuente con ese código.',
};

function ChipEstado({ enabled }: { enabled: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
        enabled
          ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
          : 'bg-slate-100 text-slate-500 ring-slate-200'
      }`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}
      />
      {enabled ? 'Activa' : 'Inactiva'}
    </span>
  );
}

// ─────────────────────────────── Formulario ────────────────────────────────

function FuenteForm({
  fuente,
  onCerrar,
  onGuardado,
}: {
  fuente: Source | null;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const esEdicion = fuente !== null;
  const [code, setCode] = useState(fuente?.code ?? '');
  const [name, setName] = useState(fuente?.name ?? '');
  const [url, setUrl] = useState(fuente?.url ?? '');
  const [section, setSection] = useState(fuente?.section ?? '');
  const [fetchType, setFetchType] = useState<FetchType>(fuente?.fetch_type ?? 'http');
  const [country, setCountry] = useState(fuente?.country ?? '');
  const [llmBrief, setLlmBrief] = useState(fuente?.llm_brief ?? '');
  const [filterConfig, setFilterConfig] = useState(
    fuente?.filter_config != null ? JSON.stringify(fuente.filter_config, null, 2) : '',
  );
  const [enabled, setEnabled] = useState(fuente?.enabled ?? true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeId = useId();
  const nameId = useId();
  const urlId = useId();
  const sectionId = useId();
  const typeId = useId();
  const countryId = useId();
  const briefId = useId();
  const filterId = useId();
  const enabledId = useId();

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (enviando) return;
    setError(null);

    let filterParsed: unknown;
    if (filterConfig.trim()) {
      try {
        filterParsed = JSON.parse(filterConfig);
      } catch {
        setError('El filtro (filter_config) no es un JSON válido.');
        return;
      }
    }

    setEnviando(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        url: url.trim(),
        section: section.trim() || null,
        fetch_type: fetchType,
        country: country.trim() || null,
        llm_brief: llmBrief.trim() || null,
        filter_config: filterConfig.trim() ? filterParsed : null,
        enabled,
      };
      if (!esEdicion) body.code = code.trim();

      if (esEdicion) {
        await apiFetch(`/api/sources/${fuente.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      } else {
        await apiFetch('/api/sources', { method: 'POST', body: JSON.stringify(body) });
      }
      onGuardado();
    } catch (err) {
      if (err instanceof ApiError && err.code && MENSAJES_ERROR[err.code]) {
        setError(MENSAJES_ERROR[err.code]);
      } else {
        setError(traducirError(err));
      }
      setEnviando(false);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}

      {/* Layout apaisado en grilla 2×2: FILA 1 identidad ↔ brief (misma altura, el brief
          crece para igualar la izquierda); FILA 2 "fuente activa" ↔ filtro (misma línea).
          En mobile se apila en una sola columna (mobile-first). */}
      <div className="grid gap-x-7 gap-y-5 md:grid-cols-2">
        {/* ── Fila 1 · izquierda: identidad y configuración ── */}
        <div className="flex flex-col gap-5">
          <div className="grid items-end gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor={codeId} className={labelClass}>
                Código
              </label>
              <input
                id={codeId}
                type="text"
                required
                value={code}
                disabled={esEdicion}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ej: sma_sanciones"
                className={inputClass}
              />
              {esEdicion && (
                <p className="mt-1.5 text-xs font-light text-gris">El código no puede modificarse.</p>
              )}
            </div>

            <div>
              <label htmlFor={typeId} className={labelClass}>
                Tipo de fetch
              </label>
              <select
                id={typeId}
                value={fetchType}
                onChange={(e) => setFetchType(e.target.value as FetchType)}
                className={inputClass}
              >
                {FETCH_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {FETCH_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor={nameId} className={labelClass}>
              Nombre
            </label>
            <input
              id={nameId}
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre de la fuente"
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor={urlId} className={labelClass}>
              URL
            </label>
            <input
              id={urlId}
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className={inputClass}
            />
          </div>

          <div className="grid items-end gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor={sectionId} className={labelClass}>
                Sección <span className="font-light lowercase tracking-normal text-gris">(opcional)</span>
              </label>
              <input
                id={sectionId}
                type="text"
                value={section}
                onChange={(e) => setSection(e.target.value)}
                placeholder="Ej: Sanciones"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor={countryId} className={labelClass}>
                País <span className="font-light lowercase tracking-normal text-gris">(opcional)</span>
              </label>
              <input
                id={countryId}
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Ej: CL"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* ── Fila 1 · derecha: brief (el textarea crece para igualar la altura de la izquierda) ── */}
        <div className="flex flex-col">
          <label htmlFor={briefId} className={labelClass}>
            Brief de terreno para Nicanor (N1)
          </label>
          <textarea
            id={briefId}
            rows={7}
            value={llmBrief}
            onChange={(e) => setLlmBrief(e.target.value)}
            placeholder="Qué leer y qué excluir en ESTA fuente: secciones relevantes, avisos a ignorar…"
            className={`${inputClass} min-h-[9rem] flex-1 resize-y leading-relaxed`}
          />
          <p className="mt-1.5 text-xs font-light leading-relaxed text-gris">
            Conocimiento de ESTA fuente en particular (qué mirar, qué ignorar, cómo es el sitio).
            Lo que aplica al oficio del agente para todas las fuentes se entrena en
            Newsletter → Entrenamiento.
          </p>
        </div>

        {/* ── Fila 2 · izquierda: fuente activa (alineada con el label de Filtro) ── */}
        <label htmlFor={enabledId} className="flex items-center gap-3 self-start">
          <input
            id={enabledId}
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-azul-profundo focus:ring-azul-profundo"
          />
          <span className="text-sm font-normal text-texto">Fuente activa</span>
        </label>

        {/* ── Fila 2 · derecha: filtro avanzado ── */}
        <div>
          <label htmlFor={filterId} className={labelClass}>
            Filtro avanzado (JSON, opcional)
          </label>
          <textarea
            id={filterId}
            rows={3}
            value={filterConfig}
            onChange={(e) => setFilterConfig(e.target.value)}
            placeholder='Ej: {"desde": "2026-01-01"}'
            spellCheck={false}
            className={`${inputClass} resize-y font-mono text-[13px] leading-relaxed`}
          />
        </div>
      </div>

      <div className="mt-1 flex justify-end gap-3 border-t border-slate-100 pt-5">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-lg px-4 py-2.5 text-sm font-medium text-gris transition-colors duration-150 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={enviando}
          className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
        >
          {enviando ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Crear fuente'}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────── Confirmación de eliminación ───────────────────────

function ConfirmarEliminar({
  fuente,
  onCerrar,
  onEliminado,
}: {
  fuente: Source;
  onCerrar: () => void;
  onEliminado: () => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onEliminar() {
    setError(null);
    setEnviando(true);
    try {
      await apiFetch(`/api/sources/${fuente.id}`, { method: 'DELETE' });
      onEliminado();
    } catch (err) {
      setError(traducirError(err));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Eliminar fuente" onCerrar={onCerrar}>
      {error && (
        <div
          role="alert"
          className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}
      <p className="text-sm font-light leading-relaxed text-texto">
        ¿Seguro que quieres eliminar la fuente{' '}
        <span className="font-medium text-azul-profundo">{fuente.name}</span>? Dejará de usarse en
        las próximas corridas.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-lg px-4 py-2.5 text-sm font-medium text-gris transition-colors duration-150 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onEliminar}
          disabled={enviando}
          className="rounded-lg bg-red-600 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {enviando ? 'Eliminando…' : 'Eliminar'}
        </button>
      </div>
    </Modal>
  );
}

// ──────────────────────────────── Sección ──────────────────────────────────

type Dialogo =
  | { tipo: 'crear' }
  | { tipo: 'editar'; fuente: Source }
  | { tipo: 'eliminar'; fuente: Source }
  | null;

export default function SeccionFuentes() {
  const [fuentes, setFuentes] = useState<Source[]>([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const [alternando, setAlternando] = useState<string | null>(null);
  /** Prueba de conexión por fuente: encola un job source-test y espera su resultado. */
  interface PruebaFuente {
    estado: 'corriendo' | 'ok' | 'error';
    chars?: number;
    ms?: number;
    extracto?: string;
    error?: string | null;
  }
  const [pruebas, setPruebas] = useState<Record<string, PruebaFuente>>({});

  async function probarFuente(id: string) {
    setPruebas((prev) => ({ ...prev, [id]: { estado: 'corriendo' } }));
    try {
      const { job } = await apiFetch<{ job: { id: string } }>(`/api/sources/${id}/test`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { job: j } = await apiFetch<{
          job: { status: string; error: string | null; detail: Record<string, unknown> | null };
        }>(`/api/newsletter/jobs/${job.id}`);
        if (j.status === 'done' || j.status === 'error') {
          const d = (j.detail ?? {}) as { chars?: number; ms?: number; extracto?: string; error?: string | null };
          setPruebas((prev) => ({
            ...prev,
            [id]: {
              estado: j.status === 'done' ? 'ok' : 'error',
              chars: d.chars,
              ms: d.ms,
              extracto: d.extracto,
              error: j.error ?? d.error ?? null,
            },
          }));
          return;
        }
      }
      setPruebas((prev) => ({ ...prev, [id]: { estado: 'error', error: 'La prueba no respondió a tiempo.' } }));
    } catch (err) {
      setPruebas((prev) => ({ ...prev, [id]: { estado: 'error', error: traducirError(err) } }));
    }
  }
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setErrorCarga(null);
    try {
      const data = await apiFetch<{ sources: Source[] }>('/api/sources');
      setFuentes(data.sources);
    } catch (err) {
      setErrorCarga(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  function cerrarYRefrescar() {
    setDialogo(null);
    void cargar();
  }

  async function alternarEnabled(f: Source) {
    setErrorAccion(null);
    setAlternando(f.id);
    try {
      await apiFetch(`/api/sources/${f.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !f.enabled }),
      });
      await cargar();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setAlternando(null);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm font-light text-gris">
          Configura las fuentes que el pipeline recolecta y el brief que guía al agente N1.
        </p>
        <button
          type="button"
          onClick={() => setDialogo({ tipo: 'crear' })}
          className="inline-flex items-center gap-2 rounded-lg bg-azul-profundo px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          Nueva fuente
        </button>
      </div>

      {errorAccion && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {errorAccion}
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {cargando ? (
          <div
            role="status"
            aria-live="polite"
            className="px-6 py-16 text-center text-sm font-light uppercase tracking-[0.2em] text-gris"
          >
            Cargando fuentes…
          </div>
        ) : errorCarga ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm text-red-700">{errorCarga}</p>
            <button
              type="button"
              onClick={() => void cargar()}
              className="mt-4 rounded-lg border border-azul-profundo px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
            >
              Reintentar
            </button>
          </div>
        ) : fuentes.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-azul-medio">
              Sin fuentes todavía
            </p>
            <p className="mt-3 text-sm font-light text-gris">
              Agrega tu primera fuente con “Nueva fuente”.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-medium uppercase tracking-[0.15em] text-gris">
                  <th scope="col" className="px-6 py-3 font-medium">
                    Fuente
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Tipo
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Estado
                  </th>
                  <th scope="col" className="px-6 py-3 font-medium">
                    Última corrida
                  </th>
                  <th scope="col" className="px-6 py-3 text-right font-medium">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fuentes.map((f) => (
                  <Fragment key={f.id}>
                  <tr className="text-sm">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-normal text-texto">{f.name}</span>
                        <span className="text-xs font-medium text-azul-medio">{f.code}</span>
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noreferrer"
                          className="max-w-[280px] truncate text-xs font-light text-gris underline-offset-2 hover:text-azul-profundo hover:underline"
                        >
                          {f.url}
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-600">
                        {FETCH_TYPE_LABEL[f.fetch_type]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <ChipEstado enabled={f.enabled} />
                    </td>
                    <td className="px-6 py-4 text-xs font-light text-gris">
                      {fmtFecha(f.last_fetched_at)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => void probarFuente(f.id)}
                          disabled={pruebas[f.id]?.estado === 'corriendo'}
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {pruebas[f.id]?.estado === 'corriendo' ? 'Probando…' : 'Probar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDialogo({ tipo: 'editar', fuente: f })}
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => void alternarEnabled(f)}
                          disabled={alternando === f.id}
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-profundo focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {alternando === f.id ? '…' : f.enabled ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDialogo({ tipo: 'eliminar', fuente: f })}
                          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                  {pruebas[f.id] && (
                    <tr>
                      <td colSpan={5} className="px-6 pb-4 pt-0">
                        {pruebas[f.id].estado === 'corriendo' ? (
                          <p className="flex items-center gap-2 text-xs font-normal text-gris">
                            <Spinner tamano="xs" /> El subagente de captura está descargando la fuente…
                          </p>
                        ) : pruebas[f.id].estado === 'ok' ? (
                          <div className="rounded-[10px] border border-emerald-200 bg-emerald-50/60 p-3">
                            <p className="text-xs font-medium text-emerald-700">
                              Conexión OK · {new Intl.NumberFormat('es-CL').format(pruebas[f.id].chars ?? 0)} caracteres extraídos · {new Intl.NumberFormat('es-CL').format(pruebas[f.id].ms ?? 0)} ms
                            </p>
                            {pruebas[f.id].extracto && (
                              <pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap rounded-lg bg-white p-2.5 font-mono text-[11px] leading-relaxed text-texto ring-1 ring-inset ring-emerald-100">{pruebas[f.id].extracto}</pre>
                            )}
                          </div>
                        ) : (
                          <p className="alerta-error">Conexión fallida: {pruebas[f.id].error ?? 'error desconocido'}</p>
                        )}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(dialogo?.tipo === 'crear' || dialogo?.tipo === 'editar') && (
        <Modal
          titulo={dialogo.tipo === 'crear' ? 'Nueva fuente' : 'Editar fuente'}
          onCerrar={() => setDialogo(null)}
          ancho="xl"
        >
          <FuenteForm
            fuente={dialogo.tipo === 'editar' ? dialogo.fuente : null}
            onCerrar={() => setDialogo(null)}
            onGuardado={cerrarYRefrescar}
          />
        </Modal>
      )}

      {dialogo?.tipo === 'eliminar' && (
        <ConfirmarEliminar
          fuente={dialogo.fuente}
          onCerrar={() => setDialogo(null)}
          onEliminado={cerrarYRefrescar}
        />
      )}
    </div>
  );
}
